/**
 * The vendor estimate table in the pop-up's center panel comes from V1 with the long line names and the vendor-form labels
 * in the description ("Standard Hourly Rate", "Proposed. Work Proposed Description: …"). Nahid (4 Nov) asked for short
 * names in the Item column and the labels taken out of the Description column.
 *
 * Pure DOM / string work on the already-parsed estimate, so it can be tested without the widget.
 */

/** Long V1 line name (lower case, single spaces, no spaces around "/") → the short name shown in the Item column. */
const SHORT_ITEM_NAMES: ReadonlyMap<string, string> = new Map([
  ['standard hourly rate', 'Labor'],
  ['overtime/weekend hourly rate', 'Labor'],
  ['helper standard hourly rate', 'Helper'],
  ['helper overtime/weekend hourly rate', 'Helper'],
  ['flat rate trip charge', 'Trip'],
  ['overtime/weekend flat rate trip charge', 'Trip'],
  ['materials', 'Materials'],
]);

const normalizeName = (name: string): string =>
  name.replace(/\s+/g, ' ').replace(/\s*\/\s*/g, '/').trim().toLowerCase();

/** The Item column's name: "Standard Hourly Rate" → "Labor", …; any other name is shown as it is. */
export function shortItemLabel(itemName: string | null | undefined): string {
  const name = (itemName ?? '').trim();
  return SHORT_ITEM_NAMES.get(normalizeName(name)) ?? name;
}

const isTripCharge = (itemName: string): boolean => /trip\s*charge/i.test(itemName);

/**
 * The Description column without the vendor-form labels:
 *   - "Work Proposed Description:", "Work Incurred Description:", "Proposed Materials Description:" and
 *     "Incurred Materials Description:" are taken out;
 *   - "Work Incurred (After Hours/ ER):" and "Work Proposed (After Hours/ ER):" keep only "(After Hours/ ER):";
 *   - a trip charge line shows only "Proposed" or "Incurred".
 */
export function cleanLineDescription(itemName: string | null | undefined, description: string | null | undefined): string {
  const text = description ?? '';
  if (isTripCharge(itemName ?? '')) {
    if (/^\s*(?:cost\s+)?incurred\b/i.test(text)) return 'Incurred';
    if (/^\s*proposed\b/i.test(text)) return 'Proposed';
    if (/\bincurred\b/i.test(text)) return 'Incurred';
    if (/\bproposed\b/i.test(text)) return 'Proposed';
    return text.trim();
  }
  return text
    .replace(/\bWork\s+(?:Proposed|Incurred)\s+Description\s*:[ \t]*/gi, '')
    .replace(/\b(?:Proposed|Incurred)\s+Materials\s+Description\s*:[ \t]*/gi, '')
    .replace(/\bWork\s+(?:Proposed|Incurred)\s*\(\s*After\s+Hours\s*\/\s*ER\s*\)\s*:/gi, '(After Hours/ ER):')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line) => line !== '')
    .join('\n');
}

/** Column positions of "Item" and "Description" in a table's header, or null when the table has neither. */
function itemColumns(table: HTMLTableElement): { item: number; description: number } | null {
  const head = table.querySelector('thead tr') ?? table.querySelector('tr');
  if (!head) return null;
  const names = Array.from(head.children).map((c) => (c.textContent ?? '').replace(/\s+/g, ' ').trim().toLowerCase());
  const item = names.indexOf('item');
  const description = names.indexOf('description');
  return item >= 0 && description >= 0 ? { item, description } : null;
}

/** Applies the short names and the cleaned descriptions to every estimate table under `root`. */
export function relabelEstimateTables(root: HTMLElement): void {
  root.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    const cols = itemColumns(table);
    if (!cols) return;
    const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr')).filter(
      (tr) => tr.closest('table') === table && !!tr.querySelector('td') && tr.cells.length > Math.max(cols.item, cols.description),
    );
    rows.forEach((tr) => {
      const itemCell = tr.cells[cols.item];
      const descCell = tr.cells[cols.description];
      const longName = (itemCell.textContent ?? '').trim();
      if (!longName) return;
      const short = shortItemLabel(longName);
      if (short !== longName) {
        itemCell.textContent = short;
        itemCell.title = longName;
      }
      const desc = descCell.textContent ?? '';
      const cleaned = cleanLineDescription(longName, desc);
      if (cleaned !== desc) descCell.textContent = cleaned;
    });
  });
}
